import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BookOpen, Menu, X, LogOut, User, ChevronDown, Settings } from 'lucide-react'
import { useAuthContext } from '../context/AuthContext'

const navItems = [
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'Catalog', href: '/catalog' },
  { label: 'Transcript', href: '/transcript' },
  { label: 'Support', href: '/support' },
]

export default function Header() {
  const { user, profile, signOut } = useAuthContext()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Close menus when user changes (login/logout) — skip initial mount
  const prevUserRef = useRef(user)
  useEffect(() => {
    if (prevUserRef.current !== user) {
      setUserMenuOpen(false)
      setMobileMenuOpen(false)
      prevUserRef.current = user
    }
  }, [user])

  // Close user menu on click outside
  useEffect(() => {
    if (!userMenuOpen) return
    function handleOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false)
    }
    document.addEventListener('click', handleOutside)
    return () => document.removeEventListener('click', handleOutside)
  }, [userMenuOpen])

  const handleSignOut = async () => {
    await signOut()
    navigate('/signin?logged_out=true')
  }

  const logoDestination = user ? '/dashboard' : '/'

  return (
    <header>
      {/* Top banner */}
      <div className="bg-neutral-50 border-b border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 py-1.5 text-xs text-neutral-500 flex items-center gap-2">
          <BookOpen size={14} aria-hidden="true" />
          <span>College External Training Application</span>
        </div>
      </div>

      {/* Logo bar */}
      <div className="bg-white border-b border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 py-4 flex items-center justify-between">
          <Link to={logoDestination} className="flex items-center" aria-label={user ? 'CETA Dashboard' : 'CETA Home'}>
            <img
              src="/logo.png"
              alt="CETA — College External Training Application"
              className="h-12 sm:h-14 w-auto"
            />
          </Link>

          {user ? (
            <div className="flex items-center gap-1">
              {/* User dropdown — desktop */}
              <div className="relative hidden sm:block" ref={userMenuRef}>
                <button
                  onClick={(e) => { e.stopPropagation(); setUserMenuOpen(!userMenuOpen) }}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-500 hover:text-primary hover:bg-neutral-50 transition-colors"
                  aria-label="User menu"
                  aria-expanded={userMenuOpen}
                >
                  <User size={16} aria-hidden="true" />
                  <span className="font-medium text-neutral-900 max-w-[150px] truncate">
                    {profile?.full_name || user.email}
                  </span>
                  <ChevronDown size={14} className={`text-neutral-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-lg border border-neutral-200 bg-white py-1 shadow-lg">
                    <Link
                      to="/account"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary transition-colors"
                    >
                      <Settings size={14} aria-hidden="true" />
                      Account Settings
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-error transition-colors cursor-pointer"
                    >
                      <LogOut size={14} aria-hidden="true" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>

              {/* Sign out — mobile (visible when no dropdown) */}
              <button
                onClick={handleSignOut}
                className="sm:hidden text-sm text-neutral-500 hover:text-error transition-colors p-2"
                aria-label="Sign out"
              >
                <LogOut size={18} />
              </button>

              {/* Mobile menu button */}
              <button
                type="button"
                className="md:hidden p-2 text-neutral-500 hover:text-primary"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-nav"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link
                to="/signup"
                className="text-sm font-semibold text-primary hover:text-primary-dark transition-colors px-4 py-2"
              >
                Sign Up
              </Link>
              <Link
                to="/signin"
                className="text-sm font-bold text-white bg-primary hover:bg-primary-dark transition-colors px-5 py-2 rounded uppercase tracking-wider"
              >
                Sign In
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Primary nav — only when logged in */}
      {user && (
        <>
          <nav className="hidden md:block bg-primary" aria-label="Primary navigation">
            <div className="mx-auto max-w-7xl px-4 flex items-center">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-white font-bold text-[0.9375rem] uppercase tracking-wider px-4 py-3 border-b-[3px] border-transparent hover:border-warning hover:bg-white/10 transition-colors"
                >
                  {item.label}
                </Link>
              ))}
              {profile?.role === 'admin' && (
                <Link
                  to="/admin"
                  className="ml-auto bg-success hover:bg-success-hover text-white font-bold text-[0.9375rem] uppercase tracking-wider px-4 py-2 rounded transition-colors"
                >
                  Admin
                </Link>
              )}
            </div>
          </nav>

          {/* Mobile nav */}
          {mobileMenuOpen && (
            <nav id="mobile-nav" className="md:hidden bg-primary" aria-label="Mobile navigation">
              <div className="px-4 py-2 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="block text-white font-semibold uppercase tracking-wider px-3 py-3 rounded hover:bg-white/10 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
                <Link
                  to="/account"
                  className="block text-white font-semibold uppercase tracking-wider px-3 py-3 rounded hover:bg-white/10 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Account Settings
                </Link>
                {profile?.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="block bg-success hover:bg-success-hover text-white font-semibold uppercase tracking-wider px-3 py-3 rounded text-center transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={() => { handleSignOut(); setMobileMenuOpen(false) }}
                  className="w-full text-left text-white/70 font-semibold uppercase tracking-wider px-3 py-3 rounded hover:bg-white/10 transition-colors border-t border-white/20 mt-2"
                >
                  Sign Out
                </button>
              </div>
            </nav>
          )}
        </>
      )}
    </header>
  )
}
